import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { MessageInstance } from 'antd/es/message/interface';
import type { ApiError, ApiResponse } from './types';

// Global reference to Ant Design message API (context-aware)
// This will be set by the App component to avoid static method warnings
let messageApi: MessageInstance | null = null;

/**
 * Set the message API instance from App context
 * This allows axios interceptors to use context-aware message methods
 */
export function setMessageApi(api: MessageInstance) {
  messageApi = api;
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token and organization context
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add current organization ID to all requests for multi-tenant scoping
    // Get from persisted organization store (localStorage key: organization-context)
    try {
      const orgContext = localStorage.getItem('organization-context');
      if (orgContext) {
        const parsed = JSON.parse(orgContext);
        const currentOrgId = parsed?.state?.currentOrganization?.id;
        if (currentOrgId && config.headers) {
          config.headers['X-Organization-ID'] = currentOrgId;
        }
      }
    } catch (error) {
      // Silently fail - organization context is optional for some endpoints (auth, etc.)
      if (import.meta.env.DEV) {
        console.warn('Failed to read organization context:', error);
      }
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Helper function to get user-friendly error message
const getErrorMessage = (error: AxiosError<ApiError>): string => {
  // Network errors (no response from server)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return 'Request timeout. Please check your connection and try again.';
    }
    if (error.code === 'ERR_NETWORK') {
      return 'Network error. Please check your internet connection.';
    }
    return 'Unable to connect to the server. Please try again later.';
  }

  const status = error.response.status;
  const data = error.response.data as ApiError | ApiResponse<any>;

  // Extract error message from response
  // API returns { success: false, error: { code: "...", message: "..." } }
  const serverMessage = (data as any)?.error?.message || data?.message;

  // Handle specific HTTP status codes with user-friendly messages
  switch (status) {
    case 400:
      return serverMessage || 'Invalid request. Please check your input and try again.';
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return serverMessage || 'You do not have permission to perform this action.';
    case 404:
      return serverMessage || 'The requested resource was not found.';
    case 409:
      return serverMessage || 'This operation conflicts with existing data.';
    case 422:
      return serverMessage || 'Validation failed. Please check your input.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      // In development, show actual server error message for debugging
      if (import.meta.env.DEV && serverMessage) {
        return `Server error: ${serverMessage}`;
      }
      return 'Server error. Our team has been notified. Please try again later.';
    case 502:
      return 'Service temporarily unavailable. Please try again in a moment.';
    case 503:
      return 'Service is currently unavailable. Please try again later.';
    case 504:
      return 'Request timeout. The server took too long to respond.';
    default:
      return serverMessage || `An error occurred (Error ${status}). Please try again.`;
  }
};

// Response interceptor - Handle errors and token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - Token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await axios.post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
          '/api/v1/auth/refresh',
          { refreshToken }
        );

        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - Clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Get user-friendly error message
    const errorMessage = getErrorMessage(error);

    // Don't show message for cancelled requests
    if (error.code !== 'ERR_CANCELED') {
      // Show error with appropriate duration based on severity
      const duration = error.response?.status && error.response.status >= 500 ? 6 : 4;

      // Use context-aware message API if available, otherwise log to console
      if (messageApi) {
        messageApi.error({
          content: errorMessage,
          duration,
          key: `error-${error.response?.status || 'network'}`, // Prevent duplicate messages
        });
      }

      // Log errors to console in development
      if (import.meta.env.DEV) {
        console.error('API Error:', {
          status: error.response?.status,
          message: errorMessage,
          url: error.config?.url,
          method: error.config?.method,
          data: error.response?.data,
        });
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
